(function () {
  const DEFAULT_API_VERSION = '2026-04';

  const CREATE_ESTIMATE_CART_MUTATION = `
    mutation FuncinatingProductShippingEstimate($input: CartInput) {
      cartCreate(input: $input) {
        cart {
          deliveryGroups(first: 10) {
            edges {
              node {
                deliveryOptions {
                  title
                  handle
                  description
                  deliveryMethodType
                  estimatedCost {
                    amount
                    currencyCode
                  }
                }
              }
            }
          }
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  function toNumber(value) {
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
  }

  function normalizeDeliveryGroups(deliveryGroups) {
    if (!deliveryGroups) return [];
    if (Array.isArray(deliveryGroups)) return deliveryGroups;
    if (Array.isArray(deliveryGroups.edges)) {
      return deliveryGroups.edges.map((edge) => edge?.node).filter(Boolean);
    }
    return [];
  }

  function pickCheapestDeliveryOption(deliveryGroups) {
    const cheapest = normalizeDeliveryGroups(deliveryGroups)
      .flatMap((group) => (Array.isArray(group?.deliveryOptions) ? group.deliveryOptions : []))
      .filter((option) => option?.deliveryMethodType === 'SHIPPING' && option?.estimatedCost?.currencyCode)
      .map((option) => ({
        title: option.title || '',
        handle: option.handle || '',
        amount: toNumber(option.estimatedCost?.amount),
        currencyCode: option.estimatedCost.currencyCode,
        description: option.description || '',
      }))
      .sort((left, right) => left.amount - right.amount)[0];

    return cheapest || null;
  }

  function readMarketSummary(payload, countryCode) {
    if (!payload || typeof payload !== 'object' || !countryCode) return null;

    const markets = payload.markets;
    if (!markets || typeof markets !== 'object') return null;

    const normalizedCountryCode = String(countryCode).toUpperCase();
    const summary = markets[normalizedCountryCode];

    return summary && typeof summary === 'object' ? summary : null;
  }

  function createShippingStateFromSummary(summary) {
    if (!summary || typeof summary !== 'object') return null;

    const shipping = summary.shipping;
    if (!shipping || typeof shipping !== 'object' || !shipping.currencyCode) {
      return {
        available: summary.available !== false,
        stockStatus: summary.stockStatus || '',
        isFreeShipping: false,
        summaryText: '',
        option: null,
      };
    }

    const amount = shipping.hasRate === false ? null : toNumber(shipping.minRateAmount);

    return {
      available: summary.available !== false,
      stockStatus: summary.stockStatus || '',
      isFreeShipping: amount !== null && amount <= 0,
      summaryText: '',
      option:
        amount === null
          ? null
          : {
              title: '',
              handle: 'metafield-summary',
              amount,
              currencyCode: shipping.currencyCode,
              description: shipping.summary || '',
            },
      freeShippingThreshold: shipping.freeShippingThreshold || null,
      freeShippingThresholdCurrencyCode: shipping.freeShippingThresholdCurrencyCode || shipping.currencyCode,
      isFreeShippingPossible: Boolean(shipping.isFreeShippingPossible),
    };
  }

  function resolveMarketLocale(locale, countryCode) {
    if (!locale) return undefined;
    if (!countryCode) return locale;

    try {
      const maximizedLocale = new Intl.Locale(String(locale).replaceAll('_', '-')).maximize();
      return new Intl.Locale(maximizedLocale, {
        region: String(countryCode).toUpperCase(),
      }).toString();
    } catch (_error) {
      return locale;
    }
  }

  function formatMoney(amount, currencyCode, options = {}) {
    const locale = resolveMarketLocale(options.locale, options.countryCode);

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'symbol',
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
      }).format(amount);
    } catch (_error) {
      return `${currencyCode} ${amount}`;
    }
  }

  async function fetchEstimatedShipping({
    storeDomain,
    accessToken,
    apiVersion = DEFAULT_API_VERSION,
    countryCode,
    merchandiseId,
    quantity = 1,
    fetchImpl = globalThis.fetch,
  }) {
    if (!storeDomain || !accessToken || !countryCode || !merchandiseId) {
      throw new Error('Missing shipping estimate configuration.');
    }

    if (typeof fetchImpl !== 'function') {
      throw new Error('Fetch implementation is not available.');
    }

    const response = await fetchImpl(`https://${storeDomain}/api/${apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': accessToken,
      },
      body: JSON.stringify({
        query: CREATE_ESTIMATE_CART_MUTATION,
        variables: {
          input: {
            buyerIdentity: {
              countryCode,
            },
            lines: [
              {
                merchandiseId,
                quantity,
              },
            ],
          },
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Shipping estimate request failed with ${response.status}.`);
    }

    const payload = await response.json();
    if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
      throw new Error(payload.errors[0]?.message || 'Storefront API returned an error.');
    }

    const cartCreate = payload?.data?.cartCreate;
    if (!cartCreate) {
      throw new Error('Storefront API returned an empty shipping estimate.');
    }

    if (Array.isArray(cartCreate.userErrors) && cartCreate.userErrors.length > 0) {
      throw new Error(cartCreate.userErrors[0]?.message || 'Shipping estimate is unavailable.');
    }

    const option = pickCheapestDeliveryOption(cartCreate.cart?.deliveryGroups);
    if (!option) {
      return {
        isFreeShipping: false,
        option: null,
      };
    }

    return {
      isFreeShipping: option.amount <= 0,
      option,
    };
  }

  globalThis.FuncinatingProductShipping = {
    createShippingStateFromSummary,
    pickCheapestDeliveryOption,
    fetchEstimatedShipping,
    formatMoney,
    readMarketSummary,
  };
})();
